import { useEffect, useState, useSyncExternalStore } from "react";
import { getDurableConversation } from "@/api/conversations";
import {
  getDesktopChatAuthScope,
  subscribeDesktopChatAuthScope,
} from "@/desktop/desktop-chat-auth-scope";
import { conversationDisplayName } from "@/lib/conversation-display";
import { useFramesStore } from "@/stores/frames-store";
import { selectFramesByType } from "@/stores/frames-selectors";
import { useConversationStore } from "@/stores/conversation-store";

const pendingTitles = new Map<string, Promise<string>>();

function requestTitle(key: string, conversationId: string): Promise<string> {
  const existing = pendingTitles.get(key);
  if (existing) return existing;
  const pending = getDurableConversation(conversationId)
    .then(conversationDisplayName)
    .finally(() => {
      if (pendingTitles.get(key) === pending) pendingTitles.delete(key);
    });
  pendingTitles.set(key, pending);
  return pending;
}

/** The same durable request title used by conversation frames and the composer. */
export function useWorkToastConversationTitle(
  conversationId: string | null,
): string | null {
  const scope = useSyncExternalStore(
    subscribeDesktopChatAuthScope,
    getDesktopChatAuthScope,
  );
  const scopeKey = JSON.stringify([scope?.accountId, scope?.epoch]);
  const frameTitle = useFramesStore((state) => {
    if (!conversationId) return null;
    const frame = selectFramesByType(state, "conversation").find(
      (candidate) =>
        candidate.metadata.conversationId === conversationId ||
        candidate.metadata.conversation_id === conversationId,
    );
    const title = frame?.title?.trim();
    return title && !title.startsWith("Chat with ")
      ? conversationDisplayName({ title })
      : null;
  });
  const latestUserMessageId = useConversationStore((state) => {
    let latest: (typeof state.messages)[number] | undefined;
    for (const message of state.messages) {
      if (
        !conversationId ||
        message.conversationId !== conversationId ||
        message.role !== "user" ||
        message.isOptimistic
      )
        continue;
      if (
        !latest ||
        message.createdAt > latest.createdAt ||
        (message.createdAt === latest.createdAt && message.id > latest.id)
      )
        latest = message;
    }
    return latest?.id;
  });
  const [revision, setRevision] = useState(0);
  const [resolved, setResolved] = useState<{
    conversationId: string;
    scopeKey: string;
    frameTitle: string | null;
    title: string;
  } | null>(null);

  useEffect(() => {
    if (!conversationId) return;
    const refresh = () => setRevision((value) => value + 1);
    window.addEventListener("denker-conversations-changed", refresh);
    return () =>
      window.removeEventListener("denker-conversations-changed", refresh);
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;
    let active = true;
    const key = JSON.stringify([
      scopeKey,
      conversationId,
      latestUserMessageId,
      frameTitle,
      revision,
    ]);
    void requestTitle(key, conversationId)
      .then((title) => {
        if (active)
          setResolved({ conversationId, scopeKey, frameTitle, title });
      })
      .catch(() => {
        // Retain the frame or last resolved title while offline.
      });
    return () => {
      active = false;
    };
  }, [conversationId, scopeKey, latestUserMessageId, frameTitle, revision]);

  return resolved?.conversationId === conversationId &&
    resolved.scopeKey === scopeKey &&
    (frameTitle === null || resolved.frameTitle === frameTitle)
    ? resolved.title
    : frameTitle;
}
